// src/services/groqFloorPlanService.js
import Groq from "groq-sdk";
import { elementTypes } from '../constants/elementTypes';

class GroqFloorPlanService {
  constructor(apiKey) {
    if (!apiKey) throw new Error('GROQ API Key is required');

    try {
      this.groq = new Groq({
        apiKey,
        dangerouslyAllowBrowser: true,
      });
      this.model = "llama3-8b-8192";
    } catch (error) {
      console.error('Error initializing Groq client:', error);
      throw error;
    }
  }

  async generateFloorPlan(requirements) {
    if (!this.groq) throw new Error('Groq client not initialized');

    try {
      const prompt = this.createPrompt(requirements);
      const completion = await this.groq.chat.completions.create({
        messages: [
          {
            role: "system",
            content: `You are an expert township planner AI assistant. Generate a township layout with proper zoning and infrastructure. Respond ONLY with a valid JSON object following this structure exactly:
            {
              "zones": [
                {
                  "type": "zone_type",
                  "position": {"x": number, "y": number},
                  "dimensions": {"width": number, "height": number},
                  "properties": {
                    "roadAccess": boolean,
                    "sidewalks": boolean
                  }
                }
              ]
            }`,
          },
          {
            role: "user",
            content: prompt,
          },
        ],
        model: this.model,
        temperature: 0.5,
        max_tokens: 2048,
      });

      const rawResponse = completion.choices[0]?.message?.content;
      if (!rawResponse) throw new Error('Empty response from AI');

      try {
        const elements = this.parseResponse(rawResponse);
        return {
          elements,
          rawResponse
        };
      } catch (parseError) {
        throw new Error(JSON.stringify({
          message: 'Failed to parse AI response',
          rawResponse
        }));
        
      }
    } catch (error) {
      console.error("Error generating floor plan:", error);
      if (error.rawResponse) {
        throw error;
      }
      throw new Error(JSON.stringify({
        message: "Failed to generate floor plan: " + error.message,
        rawResponse: error.response?.choices?.[0]?.message?.content || 'No response available'
      }));
      
    }
  }

  // src/services/groqFloorPlanService.js
// Update only the createPrompt method - rest of the file remains same

createPrompt(requirements) {
  const maxWidth = Math.floor(requirements.plotWidth / 40);
  const maxHeight = Math.floor(requirements.plotHeight / 40);
  
  const zoneRequirements = elementTypes
    .filter(type => requirements.zoneTypes.includes(type.name))
    .map(type => {
      const sizeStr = type.maxSize 
        ? `min size ${type.minSize.width}x${type.minSize.height}, max size ${type.maxSize.width}x${type.maxSize.height}`
        : `min size ${type.minSize.width}x${type.minSize.height}`;
      
      return `${type.name}: ${sizeStr}, category: ${type.category}`;
    })
    .join('\n');

  return `Generate a well-organized township layout based on these requirements:
    Plot size: ${maxWidth}x${maxHeight} units
    
    Zone types and sizes:
    ${zoneRequirements}
    Special requirements: ${requirements.specificRequirements || 'None'}

    Follow these strict urban planning principles:
    1. Only use specified zone types: ${requirements.zoneTypes.join(', ')}
    2. Position values (x,y) must be between 0 and ${maxWidth - 1}
    3. Each zone must meet its minimum size requirements
    4. Maintain EXACTLY 3 units spacing between all zones for roads
    5. Follow these placement rules:
       - Place Residential areas near Parks and Community facilities
       - Keep Industrial zones away from Residential, Schools, and Hospitals
       - Position Hospitals and Police Stations for easy access from all areas
       - Create clusters of related facilities (education, healthcare, recreation)
       - Place Green Belts as buffers between incompatible zones
       - Position Urban Forests and Eco-Ponds to support natural corridors
       - Ensure Community Lakes have good public access
       - Place Schools and Libraries in quiet areas
       - Position Supermarkets and Restaurants for convenient access
    6. Create a hierarchical road network:
       - Main boulevards between major zones
       - Secondary roads for local access
       - Green corridors connecting parks and open spaces
    7. The entire layout must fit within ${maxWidth}x${maxHeight} units
    8. Leave space at boundaries for perimeter roads
    
    Urban Design Guidelines:
    - Create community focal points around Parks and Community Halls
    - Ensure even distribution of amenities
    - Maintain view corridors to natural features
    - Prioritize pedestrian connectivity
    - Create buffer zones between different use types
    - Plan for future expansion
    
    Return ONLY a JSON object with the floor plan data.`;
}

  parseResponse(suggestion) {
    try {
      // Extract only the JSON part from the response
      const jsonMatch = suggestion.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error("No valid JSON object found in response");
      }
      
      const jsonString = jsonMatch[0];
      const data = JSON.parse(jsonString);
      
      if (!data.zones || !Array.isArray(data.zones)) {
        throw new Error("Invalid response structure: missing zones array");
      }

      const SCALE_FACTOR = 40;
      const processedZones = data.zones
        .filter(zone => elementTypes.find(t => t.name === zone.type)) // Filter out Road types
        .map(zone => {
          const typeConfig = elementTypes.find(t => t.name === zone.type);
          if (!typeConfig) throw new Error(`Invalid zone type: ${zone.type}`);

          return {
            id: Date.now() + Math.random(),
            type: zone.type,
            category: this.getCategoryFromType(zone.type),
            x: zone.position.x * SCALE_FACTOR,
            y: zone.position.y * SCALE_FACTOR,
            width: Math.min(
              Math.max(zone.dimensions.width, typeConfig.minSize.width),
              typeConfig.maxSize?.width || 8
            ) * SCALE_FACTOR,
            height: Math.min(
              Math.max(zone.dimensions.height, typeConfig.minSize.height),
              typeConfig.maxSize?.height || 8
            ) * SCALE_FACTOR,
            properties: {
              ...this.getPropertiesForType(zone.type),
              ...zone.properties,
              roadAccess: true,
              sidewalks: true
            },
            color: typeConfig.color
          };
        });

      return processedZones;
    } catch (error) {
      console.error("Error parsing AI response:", error);
      throw new Error("Invalid AI response format");
    }
  }

  getCategoryFromType(type) {
    const config = elementTypes.find(t => t.name === type);
    return config?.category || 'building';
  }

  getPropertiesForType(type, customProperties = {}) {
    const config = elementTypes.find(t => t.name === type);
    return {
      ...config?.properties,
      ...customProperties
    };
  }
}

let groqServiceInstance = null;

export const getGroqFloorPlanService = (apiKey) => {
  if (!groqServiceInstance || apiKey) {
    groqServiceInstance = new GroqFloorPlanService(apiKey);
  }
  return groqServiceInstance;
};

export default GroqFloorPlanService;